/**
 * SetupRail (BLU-1376, handover 4.1): the 252px checklist beside the step pane.
 *
 * A `nav` with a heading per group present in the walk (Connect / Your
 * products / Your garments / Printing / Try it), in the order the steps come.
 * Each item is a button: a 24px badge, the label, and a status word for the
 * states the badge alone does not spell out (Up next, Skipped, Locked,
 * Waiting). Done and To do get the word for screen readers only; the tick and
 * the plain number carry it visually.
 *
 * The selected item has `aria-current="step"` and the brand tint fill plus a
 * full border, never a side stripe (DESIGN 10). A locked item stays clickable:
 * the pane explains the lock and offers the way to it.
 *
 * `variant="rail"` pins under the band at
 * `calc(var(--top-header-height) + var(--page-action-bar-height))`, the same
 * offset InventoryTableList uses to sit under the bar. `variant="inline"` is
 * the phone list StepPicker opens: full width, not sticky.
 */
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { StepId } from '../../lib/steps';
import type { StepGroup, StepVisualState } from '../../lib/step-state';
import { StepBadge } from './StepBadge';

export interface SetupRailItem {
  id: StepId;
  number: number;
  label: string;
  group: StepGroup;
  state: StepVisualState;
}

interface SetupRailProps {
  items: readonly SetupRailItem[];
  selectedId: StepId | null;
  onSelect: (id: StepId) => void;
  variant?: 'rail' | 'inline';
  className?: string;
}

const RAIL_TOP = 'calc(var(--top-header-height, 3.5rem) + var(--page-action-bar-height, 0px))';

/** States whose word is shown; the rest are spoken only (the badge shows them). */
const VISIBLE_WORD: ReadonlySet<StepVisualState> = new Set(['current', 'skipped', 'locked', 'waiting']);

const WORD_CLASS: Partial<Record<StepVisualState, string>> = {
  current: 'text-primary-strong',
  skipped: 'text-muted-foreground',
  locked: 'text-muted-foreground',
  waiting: 'text-amber-800',
};

/** Consecutive runs of items that share a group, in walk order. */
function groupRuns(items: readonly SetupRailItem[]): { group: StepGroup; items: SetupRailItem[] }[] {
  const runs: { group: StepGroup; items: SetupRailItem[] }[] = [];
  for (const item of items) {
    const last = runs[runs.length - 1];
    if (last && last.group === item.group) last.items.push(item);
    else runs.push({ group: item.group, items: [item] });
  }
  return runs;
}

export function SetupRail({ items, selectedId, onSelect, variant = 'rail', className }: SetupRailProps) {
  const { t } = useTranslation('onboarding');
  const baseId = useId();
  const rail = variant === 'rail';

  return (
    <nav
      aria-label={t('rail.label')}
      className={cn(
        rail ? 'sticky w-[252px] shrink-0 self-start overflow-y-auto overscroll-contain' : 'w-full',
        className,
      )}
      style={rail ? { top: RAIL_TOP, maxHeight: `calc(100dvh - ${RAIL_TOP})` } : undefined}
    >
      <div className="space-y-4 p-1">
        {groupRuns(items).map((run, index) => {
          const headingId = `${baseId}-g${index}`;
          return (
            <div key={`${run.group}-${index}`}>
              <h2
                id={headingId}
                className="px-2.5 pb-1.5 text-[11px] font-semibold uppercase leading-4 tracking-wide text-muted-foreground"
              >
                {t(`shell.group.${run.group}`)}
              </h2>
              <ul aria-labelledby={headingId} className="space-y-0.5">
                {run.items.map((item) => {
                  const selected = item.id === selectedId;
                  const showWord = VISIBLE_WORD.has(item.state);
                  return (
                    <li key={item.id}>
                      <button
                        type="button"
                        aria-current={selected ? 'step' : undefined}
                        data-state={item.state}
                        onClick={() => onSelect(item.id)}
                        className={cn(
                          'flex w-full items-center gap-2.5 rounded-lg border px-2.5 py-2 text-left',
                          'transition-colors duration-150 ease-out motion-reduce:transition-none',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                          selected
                            ? 'border-threadheads-purple/40 bg-accent'
                            : 'border-transparent hover:bg-muted/70',
                        )}
                      >
                        <StepBadge number={item.number} state={item.state} size="rail" />
                        <span className="min-w-0 flex-1">
                          <span
                            className={cn(
                              'block text-sm leading-5 [overflow-wrap:anywhere]',
                              selected ? 'font-semibold text-foreground' : 'font-medium',
                              !selected && (item.state === 'locked' ? 'text-muted-foreground' : 'text-foreground'),
                            )}
                          >
                            {item.label}
                          </span>
                          <span
                            className={cn(
                              showWord ? 'block text-xs leading-4' : 'sr-only',
                              WORD_CLASS[item.state],
                            )}
                          >
                            {t(`shell.pill.${item.state}`)}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </nav>
  );
}
